import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { ExportStatus } from '../types';

const LANG_NAME = { en: 'English', ur: 'Urdu' } as const;

/**
 * "Download video": the server records the film exactly as the player plays
 * it (in a hidden Chrome on this computer), in the language chosen here,
 * then offers the MP4. It takes as long as the film. One video per episode;
 * a new one replaces it. Leaving the page and coming back picks the progress up.
 */
export default function DownloadVideoButton({
  episodeId,
  lang,
  tone = 'panel',
}: {
  episodeId: string;
  /** The language on the story's switch (null for a film without a story). */
  lang: 'en' | 'ur' | null;
  /** 'panel' on the episode page; 'film' on the player's ending card. */
  tone?: 'panel' | 'film';
}) {
  const [status, setStatus] = useState<ExportStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const began = useRef<number | null>(null);

  const busy = starting || status?.state === 'starting' || status?.state === 'recording';

  useEffect(() => {
    let alive = true;
    api
      .exportStatus(episodeId)
      .then((s) => alive && setStatus(s))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [episodeId]);

  // Follow the recording while it runs.
  useEffect(() => {
    if (!(status?.state === 'starting' || status?.state === 'recording')) return;
    const t = window.setTimeout(() => {
      api
        .exportStatus(episodeId)
        .then((s) => {
          setStatus(s);
          if (s.state === 'error') setError(s.error ?? 'Making the video failed. Try again.');
        })
        .catch(() => setStatus((prev) => (prev ? { ...prev } : prev)));
    }, 2000);
    return () => window.clearTimeout(t);
  }, [status, episodeId]);

  const start = useCallback(async () => {
    setError(null);
    setStarting(true);
    began.current = null;
    try {
      setStatus(await api.startExport(episodeId, lang ?? 'en'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setStarting(false);
    }
  }, [episodeId, lang]);

  const film = tone === 'film';
  const primary = film
    ? 'focus-ring rounded-full bg-[#f3ece2]/10 px-6 py-2.5 text-sm font-semibold text-[#f3ece2] ring-1 ring-[#f3ece2]/15 transition hover:bg-[#f3ece2]/20'
    : 'focus-ring rounded-md bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25';
  const quiet = film
    ? 'focus-ring rounded-full px-4 py-2.5 text-sm text-[#f3ece2]/60 hover:text-[#f3ece2]'
    : 'focus-ring rounded-md px-3 py-2 text-sm text-white/55 hover:text-white';
  const note = film ? 'text-xs text-[#f3ece2]/55' : 'text-xs text-white/50';
  const langName = lang ? LANG_NAME[lang] : null;

  if (busy) {
    const pct = status?.state === 'recording' ? status.progress : 0;
    if (status?.state === 'recording' && began.current === null) began.current = Date.now() - pct * 10;
    let left: string | null = null;
    if (began.current !== null && pct >= 5) {
      const min = Math.ceil(((Date.now() - began.current) / pct) * (100 - pct) / 60000);
      left = min <= 1 ? 'about a minute left' : `about ${min} min left`;
    }
    return (
      <div className="flex min-w-[14rem] flex-col gap-1.5" role="status" aria-live="polite">
        <div className={`flex items-center justify-between gap-3 text-sm ${film ? 'text-[#f3ece2]/85' : 'text-white/80'}`}>
          <span>{status?.state === 'recording' ? `Making the video… ${pct}%` : 'Getting the video ready…'}</span>
          {left && <span className={note}>{left}</span>}
        </div>
        <div className={`h-1.5 overflow-hidden rounded-full ${film ? 'bg-[#f3ece2]/15' : 'bg-white/15'}`}>
          <div className={`h-full rounded-full transition-[width] duration-700 ${film ? 'bg-[#f3ece2]' : 'bg-white'}`} style={{ width: `${Math.max(3, pct)}%` }} />
        </div>
        <p className={note}>The film plays once in the background, so this takes about as long as the film. You can leave this page.</p>
      </div>
    );
  }

  const file = status?.file ?? null;
  const ready = file !== null && file.lang === lang;
  const fileLang = file?.lang ? LANG_NAME[file.lang] : null;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        {ready ? (
          <>
            <a href={api.exportFileUrl(episodeId)} download className={primary}>
              ⬇ Download video
            </a>
            <button onClick={() => void start()} className={quiet} aria-description="Makes the video again and replaces this one">
              Make again
            </button>
          </>
        ) : (
          <button onClick={() => void start()} className={primary}>
            Download video
          </button>
        )}
      </div>
      <p className={note}>
        {ready
          ? `${status?.state === 'done' ? 'Ready: ' : ''}${fileLang ? `${fileLang} · ` : ''}${Math.floor(file.durationSeconds / 60)}:${String(file.durationSeconds % 60).padStart(2, '0')} · ${Math.max(1, Math.round(file.bytes / 1e6))} MB`
          : `${langName ? `In ${langName}` : 'The whole film'}${fileLang && file?.lang !== lang ? `, replacing the ${fileLang} video` : ''}. Takes about as long as the film.`}
      </p>
      {error && (
        <p role="alert" className={film ? 'text-xs text-amber-200/90' : 'text-xs text-red-300'}>
          {error}
        </p>
      )}
    </div>
  );
}
